import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import {
  Loader2, Twitch as TwitchIcon, Radio, Users, Eye, Calendar, ArrowLeft, Play, ExternalLink,
} from 'lucide-react';

function formatCount(n) {
  if (n == null) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
}

function formatDuration(d) {
  if (!d) return '';
  const m = d.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
  if (!m) return d;
  const h = parseInt(m[1] || '0', 10);
  const mi = parseInt(m[2] || '0', 10);
  const s = parseInt(m[3] || '0', 10);
  const pad = (x) => String(x).padStart(2, '0');
  return h > 0 ? `${h}:${pad(mi)}:${pad(s)}` : `${mi}:${pad(s)}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days > 30) return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  if (days > 0) return `${days}d ago`;
  const hrs = Math.floor(diff / 3_600_000);
  if (hrs > 0) return `${hrs}h ago`;
  const mins = Math.floor(diff / 60_000);
  return mins > 0 ? `${mins}m ago` : 'just now';
}

export default function Twitch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await base44.functions.invoke('getTwitchFeed', {});
        if (mounted) setData(res.data);
      } catch (_) {
        if (mounted) setData({ connected: false });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const parent = typeof window !== 'undefined' ? window.location.hostname : 'motion-reality-website.base44.app';
  const channel = data?.login;
  const playerSrc = channel
    ? `https://player.twitch.tv/?channel=${channel}&parent=${parent}&muted=false&autoplay=false`
    : '';
  const thumb = (url) => (url || '').replace('%{width}', '640').replace('%{height}', '360');

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Hero */}
      <section className="px-6 lg:px-10 py-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO HOME
          </Link>

          <p className="text-primary text-xs font-bold tracking-widest mb-2 flex items-center gap-1.5">
            <TwitchIcon className="w-4 h-4" /> LIVE STREAM
          </p>
          <h1 className="text-white font-black text-3xl md:text-5xl uppercase">Twitch Feed</h1>

          {loading ? (
            <div className="flex items-center gap-3 text-white/40 mt-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /> Loading your channel…</div>
          ) : !data?.connected ? (
            <div className="mt-8 flex items-center gap-3 border border-white/5 bg-[#111115] p-4 max-w-md">
              <TwitchIcon className="w-6 h-6 text-white/20" />
              <p className="text-white/40 text-sm">Twitch isn't connected yet. Connect it from the admin dashboard to show the live feed here.</p>
            </div>
          ) : (
            <div className="flex items-center gap-5 flex-wrap mt-6">
              {data.profile_image_url && (
                <img src={data.profile_image_url} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-primary" />
              )}
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-bold text-2xl">{data.display_name}</span>
                  {data.is_live ? (
                    <span className="flex items-center gap-1.5 bg-red-500/15 border border-red-500/40 text-red-400 text-[10px] font-bold tracking-widest px-2 py-1">
                      <Radio className="w-3 h-3 animate-pulse" /> LIVE
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/40 text-[10px] font-bold tracking-widest px-2 py-1">OFFLINE</span>
                  )}
                </div>
                <p className="text-white/40 text-sm mt-1 max-w-xl">{data.stream_title || data.description || `twitch.tv/${data.login}`}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-white/50 flex-wrap">
                  {typeof data.followers === 'number' && (
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {formatCount(data.followers)} followers</span>
                  )}
                  {data.is_live && typeof data.viewer_count === 'number' && (
                    <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {formatCount(data.viewer_count)} watching</span>
                  )}
                  {data.game_name && <span className="text-primary font-bold">{data.game_name}</span>}
                  <a href={`https://www.twitch.tv/${data.login}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-white/40 hover:text-primary transition-colors">
                    twitch.tv/{data.login} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Player */}
      {data?.connected && channel && (
        <section className="px-6 lg:px-10 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="aspect-video bg-black border border-white/10 overflow-hidden">
              <iframe src={playerSrc} allowFullScreen className="w-full h-full" title="Twitch live stream" />
            </div>
            {!data.is_live && data.videos?.length > 0 && (
              <p className="text-white/40 text-xs mt-3 text-center">We're offline right now — catch up on a recent broadcast below.</p>
            )}
          </div>
        </section>
      )}

      {/* Past broadcasts */}
      {data?.connected && (
        <section className="px-6 lg:px-10 py-10 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-white font-black text-xl uppercase mb-5">Past Broadcasts</h2>
            {data.videos?.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.videos.map((v) => (
                  <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" className="group border border-white/5 bg-[#111115] overflow-hidden hover:border-primary/40 transition-colors">
                    <div className="relative aspect-video bg-black overflow-hidden">
                      <img src={thumb(v.thumbnail_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5">{formatDuration(v.duration)}</span>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                        <Play className="w-10 h-10 text-white" fill="currentColor" />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-white text-sm font-bold line-clamp-2 group-hover:text-primary transition-colors">{v.title}</p>
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-white/40">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatCount(v.view_count)}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {timeAgo(v.created_at)}</span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-white/30 text-sm">No past broadcasts available yet.</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}